import { api } from './client';

export type SessionVerdict = 'bot' | 'human' | 'unsure';
/** Разметка для обучения: бот по причине блока, человек по регистрации/депозиту. */
export type SessionLabel = 'bot' | 'human' | 'conflict' | null;

/**
 * Последний результат проверки «человек или бот» по устройству
 * (/device/sessioninfo). sdk_status / block_reason / модель — снимок строки
 * устройства в момент результата; current_* — её состояние сейчас.
 */
export interface SessionCheck {
  id: string;
  device_id: string;
  bundle_id: string;
  bot_probability: number;
  confidence: number;
  verdict: SessionVerdict;
  sdk_status: string | null;
  block_reason: string | null;
  device_model: string | null;
  os_version: string | null;
  ip_country: string | null;
  is_test: number;
  created_at: string;
  /** Сколько результатов всего пришло от устройства (в тестовом режиме SDK — по одному на запуск). */
  results: number;
  current_status: string | null;
  current_reason: string | null;
  label: SessionLabel;
  /** stats из /device/sessioninfo как пришли (JSON); null — старый SDK со своей оценкой. */
  stats: string | null;
  /** 'rules', 'model:<id>', 'sdk' (оценка телефона), 'unscored_v2'… */
  scored_by: string | null;
  /** Сработавшие правила через запятую. */
  signals: string | null;
  /** Кто попросил проверку: auth / precheck / report / report_clean. */
  asked_by: string | null;
}

export interface VerdictCounts {
  total: number;
  human: number;
  bot: number;
  unsure: number;
}

export interface SessionThresholds {
  bot_min: number;
  bot_min_confidence: number;
  human_max: number;
  human_min_confidence: number;
}

export interface ModelMetrics {
  rows: number;
  bots: number;
  humans: number;
  folds: number;
  auc: number;
  at_thresholds: {
    bot_min: number;
    human_max: number;
    bots_as_human: number;
    humans_as_human: number;
    humans_as_bot: number;
    bots_as_bot: number;
  };
  splits: Record<string, number>;
}

export interface ModelSummary {
  id: string;
  trained_at: string;
  trees: number;
  stats_v: number;
  metrics: ModelMetrics | null;
}

export interface SessionCheckStats extends VerdictCounts {
  by_reason: Record<string, VerdictCounts>;
  human_pass: boolean;
  behavior_gate: boolean;
  sample_sure_bot: number;
  sample_clean: number;
  thresholds: SessionThresholds;
  model: ModelSummary | null;
}

export interface SessionModelState {
  active: ModelSummary | null;
  candidate: ModelSummary | null;
  labels: { bot: number; human: number; conflict: number; none: number };
  min_per_class: number;
}

export interface SessionCheckListResponse {
  checks: SessionCheck[];
  total: number;
  page: number;
  limit: number;
}

export interface SessionCheckQuery {
  scope?: 'blocked' | 'clean' | 'all';
  verdict?: string[];
  label?: string[];
  reason?: string[];
  bundle?: string[];
  test?: 'exclude' | 'only' | 'all';
  search?: string;
  dateFrom?: string;
  dateTo?: string;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
  page?: number;
  limit?: number;
}

function buildFilterParams(params: SessionCheckQuery): URLSearchParams {
  const query = new URLSearchParams();
  const setMulti = (key: string, vals?: string[]) => {
    if (vals && vals.length > 0) query.set(key, vals.join(','));
  };
  setMulti('verdict', params.verdict);
  setMulti('label', params.label);
  setMulti('reason', params.reason);
  setMulti('bundle', params.bundle);
  // scope и test на сервере по умолчанию blocked / exclude — шлём всегда.
  query.set('scope', params.scope || 'blocked');
  query.set('test', params.test || 'exclude');
  if (params.search) query.set('search', params.search);
  if (params.dateFrom) query.set('date_from', params.dateFrom);
  if (params.dateTo) query.set('date_to', params.dateTo);
  return query;
}

export async function getSessionChecks(params: SessionCheckQuery): Promise<SessionCheckListResponse> {
  try {
    const query = buildFilterParams(params);
    if (params.sortBy) query.set('sort_by', params.sortBy);
    if (params.sortDir) query.set('sort_dir', params.sortDir);
    if (params.page) query.set('page', String(params.page));
    if (params.limit) query.set('limit', String(params.limit));
    return await api.get<SessionCheckListResponse>(`/admin/devices/session-checks?${query.toString()}`);
  } catch {
    return { checks: [], total: 0, page: 1, limit: 50 };
  }
}

export async function getSessionCheckStats(params: SessionCheckQuery = {}): Promise<SessionCheckStats | null> {
  try {
    return await api.get<SessionCheckStats>(`/admin/devices/session-checks/stats?${buildFilterParams(params).toString()}`);
  } catch {
    return null;
  }
}

export async function getSessionCheckFacets(): Promise<{ bundles: { value: string; count: number }[] }> {
  try {
    return await api.get<{ bundles: { value: string; count: number }[] }>('/admin/devices/session-checks/facets');
  } catch {
    return { bundles: [] };
  }
}

export async function getSessionModel(): Promise<SessionModelState | null> {
  try {
    return await api.get<SessionModelState>('/admin/devices/session-checks/model');
  } catch {
    return null;
  }
}

/** Обучить кандидата. Ошибка (мало разметки) приходит текстом. */
export function trainSessionModel(): Promise<{ candidate: ModelSummary; labels: SessionModelState['labels'] }> {
  return api.post('/admin/devices/session-checks/model/train', {});
}

export function activateSessionModel(): Promise<{ active: ModelSummary; rescored: number }> {
  return api.post('/admin/devices/session-checks/model/activate', {});
}

export function deactivateSessionModel(): Promise<{ active: null; rescored: number }> {
  return api.post('/admin/devices/session-checks/model/deactivate', {});
}
